import { Card, Chip, faDigits } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type ReconciliationReportRow, type SiteRow } from "../../api.js";

export function ReconciliationTab() {
  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => api<SiteRow[]>("/sites"),
  });

  const kitchens = (sites.data ?? []).filter((s) => s.kind === "kitchen");
  const [selectedKitchenId, setSelectedKitchenId] = useState<string>("");
  const [date, setDate] = useState<string>("1405-08-01");

  const currentKitchenId = selectedKitchenId || (kitchens[0]?.id ?? "");

  const reconciliation = useQuery({
    queryKey: ["inventory", "reconciliation", currentKitchenId, date],
    queryFn: () =>
      api<ReconciliationReportRow>(`/inventory/reconciliation?kitchenId=${currentKitchenId}&date=${date}`),
    enabled: Boolean(currentKitchenId && date),
  });

  const report = reconciliation.data;
  const items = report?.items ?? [];
  const warningsCount = items.filter((i) => i.exceedsThreshold).length;

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="cluster" style={{ gap: "var(--s3)", alignItems: "center" }}>
        <div className="field">
          <label>آشپزخانه</label>
          <select
            value={currentKitchenId}
            onChange={(e) => {
              setSelectedKitchenId(e.target.value);
            }}
            className="input"
          >
            {kitchens.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>تاریخ کاری</label>
          <input
            type="text"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
            }}
            className="input"
            dir="ltr"
            style={{ width: "130px" }}
          />
        </div>

        <div style={{ marginTop: "auto" }}>
          {warningsCount > 0 ? (
            <Chip tone="coral">{`هشدار کسری: ${faDigits(warningsCount)} مورد بیش از حد مجاز`}</Chip>
          ) : (
            <Chip tone="ok">همه مقادیر در محدوده مجاز</Chip>
          )}
        </div>
      </div>

      {warningsCount > 0 && (
        <div
          style={{
            padding: "var(--s3)",
            backgroundColor: "rgba(239, 68, 68, 0.15)",
            color: "#b91c1c",
            borderRadius: "var(--radius-s)",
            fontWeight: "bold",
          }}
        >
          توجه: در {faDigits(warningsCount)} ردیف، اختلاف تولید با تحویل و مصرف پرسنل از آستانه مجاز (۵٪)
          فراتر رفته است!
        </div>
      )}

      <Card>
        <table className="table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>نام غذا</th>
              <th>برنامه‌ریزی (پرس)</th>
              <th>تولیدشده (پرس)</th>
              <th>تحویل‌شده (پرس)</th>
              <th>مصرف پرسنل (پرس)</th>
              <th>ضایعات / کسری (پرس)</th>
              <th>وضعیت تطابق</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--muted)" }}>
                  داده‌ای برای این روز و آشپزخانه یافت نشد
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.menuItemId}
                  style={item.exceedsThreshold ? { backgroundColor: "rgba(239, 68, 68, 0.08)" } : undefined}
                >
                  <td>
                    <strong>{item.foodName}</strong>
                  </td>
                  <td>{faDigits(item.plannedPortions)}</td>
                  <td>{faDigits(item.producedPortions)}</td>
                  <td>{faDigits(item.deliveredPortions)}</td>
                  <td>{faDigits(item.staffMealPortions)}</td>
                  <td
                    style={{
                      fontWeight: "bold",
                      color:
                        item.differencePortions > 0
                          ? "#dc2626"
                          : item.differencePortions < 0
                            ? "#d97706"
                            : "#16a34a",
                    }}
                  >
                    {item.differencePortions > 0 ? "+" : ""}
                    {faDigits(item.differencePortions)}
                  </td>
                  <td>
                    {item.exceedsThreshold ? (
                      <Chip tone="coral">کسری غیرمجاز ⚠️</Chip>
                    ) : (
                      <Chip tone="ok">طبیعی ✓</Chip>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
